import { useFormik } from "formik";
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useState,
} from "react";
import { Days } from "../company/work-order/component/Days";
import { Checkbox, Input } from "sr/helpers";
import { FormattedMessage } from "react-intl";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { createAvailability } from "sr/utils/api/cleaner/createAvailability";
import ContentWithSpinner from "sr/layout/common/ContentWithSpiner";
import { LoaderFullScreen } from "sr/layout/common/LoaderFullScreen";
import { getAvailability } from "sr/utils/api/cleaner/getAvailability";
import { updateAvailability } from "sr/utils/api/cleaner/updateAvailability";
import { useNavigate } from "react-router-dom";
import { ButtonAuth } from "sr/helpers/ui-components/ButtonAuth";
import availabilitySchema from "sr/utils/schemas/cleaner/availabilitySchema";
import { useValidationErrorHandle } from "sr/helpers/functions/validationErrorHandle";
import { ReactTimePicker } from "sr/helpers/ui-components/ReactTimePicker";
import { getIndIntersted } from "sr/utils/api/cleaner/indInterested/getIndIntersted";

const Availability: ForwardRefRenderFunction<any, any> = (
  {
    HandleStepper = null,
    page = "",
    type = "",
    dataAddedHandle = null,
    id = "",
  },
  ref,
) => {
  const [Availability, setAvailability] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loader, setLoader] = useState(true);
  const [buttonLoader, setButtonLoader] = useState(false);
  // eslint-disable-next-line @typescript-eslint/ban-types
  const [customError, setCustomError] = useState<{}>();
  const [isAdded, setIsAdded] = useState(false);
  const [resInterested, setResInterested] = useState(false);
  const [allWeek, setAllWeek] = useState(false);
  const [sameTimeAllDays, setSameTimeAllDays] = useState(false);
  const [sharedStartTime, setSharedStartTime] = useState("");
  const [sharedEndTime, setSharedEndTime] = useState("");
  const fullDayStart = "00:00";
  const fullDayEnd = "23:59";

  const navigate = useNavigate();
  const validationErrorHandle = useValidationErrorHandle();
  const updateAvailabilityState = (updater) => {
    setAvailability((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const allSelected = Days.every((day) =>
        next?.some((item) => item?.day === day.fullName),
      );
      if (allWeek !== allSelected) {
        setAllWeek(allSelected);
      }
      return next;
    });
  };
  const applySharedTimes = (
    list,
    startTime?: string,
    endTime?: string,
    force = false,
  ) => {
    if (!sameTimeAllDays && !force) return list;
    const start = startTime ?? sharedStartTime;
    const end = endTime ?? sharedEndTime;
    if (!start && !end) return list;
    return list.map((item) => ({
      ...item,
      start_time: start || item.start_time,
      end_time: end || item.end_time,
    }));
  };

  useEffect(() => {
    const getdetails = getUserDetails();
    setUserData(getdetails);
  }, []);
  useEffect(() => {
    const fetchAvailability = async () => {
      const params = new URLSearchParams();
      params.append("cleaner_id", id);
      try {
        const res = await getAvailability(params);
        if (res?.success === true) {
          const availabilityData = res?.data[0]?.availability || [];
          updateAvailabilityState(availabilityData);
          if (availabilityData?.length > 0) {
            const hasAllDays = Days.every((day) =>
              availabilityData?.some((item) => item?.day === day.fullName),
            );
            const firstStart = availabilityData?.[0]?.start_time;
            const firstEnd = availabilityData?.[0]?.end_time;
            const uniformTimes = availabilityData?.every(
              (item) =>
                item?.start_time === firstStart && item?.end_time === firstEnd,
            );
            if (hasAllDays && uniformTimes) {
              setSameTimeAllDays(true);
              setSharedStartTime(firstStart || "");
              setSharedEndTime(firstEnd || "");
            }
            setIsAdded(true);
          }
        } else {
          return null;
        }
      } catch (e: any) {
        // eslint-disable-next-line no-empty
      }
    };

    if (id) {
      fetchAvailability();
    }
  }, [id]);
  const initialValues = {
    cleaner_id: userData?.user_id,
    availability: Availability,
  };
  const handleAvailability = (params) => {
    setCustomError({});
    switch (params?.type) {
      case "day":
        updateAvailabilityState((prev) => {
          const alreadySelected = prev?.some(
            (item) => item?.day === params?.day,
          );
          let updated = alreadySelected
            ? prev?.filter((item) => item?.day !== params?.day)
            : [
                ...prev,
                {
                  day: params?.day,
                  start_time: sameTimeAllDays
                    ? sharedStartTime || fullDayStart
                    : fullDayStart,
                  end_time: sameTimeAllDays
                    ? sharedEndTime || fullDayEnd
                    : fullDayEnd,
                },
              ];
          updated = applySharedTimes(updated);
          return updated;
        });
        break;
      case "startTime":
        updateAvailabilityState((allObj) =>
          allObj?.map((dayObj) =>
            dayObj.day === params?.day
              ? { ...dayObj, start_time: params?.startTime }
              : dayObj,
          ),
        );
        break;
      case "endTime":
        updateAvailabilityState((allObj) =>
          allObj?.map((dayObj) =>
            dayObj.day === params?.day
              ? { ...dayObj, end_time: params?.endTime }
              : dayObj,
          ),
        );
        break;
      case "allWeek":
        if (params.allWeek) {
          const allDaysSelected = Days.map((day) => ({
            day: day.fullName,
            start_time: sameTimeAllDays
              ? sharedStartTime || fullDayStart
              : fullDayStart,
            end_time: sameTimeAllDays
              ? sharedEndTime || fullDayEnd
              : fullDayEnd,
          }));
          updateAvailabilityState(applySharedTimes(allDaysSelected));
        } else {
          setAllWeek(false);
          updateAvailabilityState([]);
        }
        break;
      case "sameTimeAll": {
        const baselineStart =
          sharedStartTime || Availability?.[0]?.start_time || "";
        const baselineEnd = sharedEndTime || Availability?.[0]?.end_time || "";
        setSameTimeAllDays(params?.value);
        if (params?.value) {
          setSharedStartTime(baselineStart);
          setSharedEndTime(baselineEnd);
          updateAvailabilityState((prev) =>
            applySharedTimes(prev, baselineStart, baselineEnd, true),
          );
        }
        break;
      }
      case "sharedStart":
        setSharedStartTime(params?.value);
        if (sameTimeAllDays) {
          updateAvailabilityState((prev) =>
            prev.map((item) => ({ ...item, start_time: params?.value })),
          );
        }
        break;
      case "sharedEnd":
        setSharedEndTime(params?.value);
        if (sameTimeAllDays) {
          updateAvailabilityState((prev) =>
            prev.map((item) => ({ ...item, end_time: params?.value })),
          );
        }
        break;
    }
  };
  useEffect(() => {
    if (!sameTimeAllDays) return;
    if (Availability?.length) {
      setSharedStartTime(Availability[0]?.start_time || "");
      setSharedEndTime(Availability[0]?.end_time || "");
    }
  }, [sameTimeAllDays, Availability]);
  const { errors, submitForm, handleSubmit } = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: availabilitySchema,
    onSubmit: async (values) => {
      for (let i = 0; i < values?.availability?.length; i++) {
        if (
          !values?.availability[i]?.start_time ||
          !values?.availability[i]?.end_time
        ) {
          const key = values?.availability[i]?.day;
          const obj = key
            ? { [key]: "Please select both start and end times." }
            : {};
          setCustomError(obj);
          return;
        }

        const dayName = values?.availability[i].day;

        const [sh, sm] = values?.availability[i]?.start_time
          .split(":")
          .map(Number);
        const [eh, em] = values?.availability[i]?.end_time
          .split(":")
          .map(Number);
        const startMins = sh * 60 + sm;
        const endMins = eh * 60 + em;

        if (startMins >= endMins) {
          setCustomError({
            [dayName]: "Start time must be earlier than End time.",
          });
          return;
        }
      }

      setButtonLoader(true);
      try {
        let res;
        if (isAdded) {
          res = await updateAvailability(values);
        } else {
          res = await createAvailability(values);
        }
        if (res?.success) {
          setButtonLoader(false);
          if (page === "popup") {
            dataAddedHandle(true);
          } else {
            navigate("/cleaner-jobs");
          }
        } else {
          setButtonLoader(false);
        }
      } catch (e: any) {
        setButtonLoader(false);
      }
    },
  });
  const checkIncluded = (fullName) => {
    return Availability?.some((item) => item?.day === fullName);
  };
  React.useImperativeHandle(ref, () => ({
    submitForm,
  }));

  const getInterested = async (cleaner_id) => {
    setLoader(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("cleaner_id", cleaner_id);
      const res = await getIndIntersted(queryParams);
      if (res?.success === true) {
        setResInterested(res?.data?.interested);
      }
    } finally {
      setLoader(false);
    }
  };
  useEffect(() => {
    if (userData?.user_id) {
      getInterested(userData?.user_id);
    } else if (userData !== null) {
      setLoader(false);
    }
  }, [userData?.user_id]);

  return (
    <ContentWithSpinner loader={loader}>
      {buttonLoader && <LoaderFullScreen />}
      {resInterested === false && (
        <Checkbox
          disabled={true}
          divclass="py-3"
          id={"interested"}
          onChange={() => setResInterested(!resInterested)}
          checked={resInterested === false}
          value={resInterested}
          label={
            <FormattedMessage id="CLEANER.JOB.TYPE.RESIDENTIAL.INTERESTED"></FormattedMessage>
          }
        ></Checkbox>
      )}
      {!resInterested ? (
        (page === "popup" && type === "add") || type === "put" ? (
          ""
        ) : (
          <ButtonAuth
            onClick={() => navigate("/cleaner-jobs")}
            loader={buttonLoader}
          >
            <FormattedMessage
              id="REGISTER.BUTTON.SAVEANDCONTINUE"
              defaultMessage="Save and Continue"
            />
          </ButtonAuth>
        )
      ) : (
        <div>
          <div className="flex space-x-3 w-full my-4 items-center">
            <Checkbox
              id={"allWeek"}
              checked={allWeek}
              onChange={() =>
                handleAvailability({ allWeek: !allWeek, type: "allWeek" })
              }
              label="All Week"
            />
          </div>
          <div className="flex space-x-3 w-full mb-4 items-center">
            <Checkbox
              id={"sameTimeAll"}
              checked={sameTimeAllDays}
              onChange={() =>
                handleAvailability({
                  type: "sameTimeAll",
                  value: !sameTimeAllDays,
                })
              }
              label="I am working the same time on all the days I selected"
            />
          </div>
          {sameTimeAllDays && (
            <div className="flex space-x-3 w-full mb-6 items-center">
              <ReactTimePicker
                onChange={(value: string) =>
                  handleAvailability({ type: "sharedStart", value })
                }
                label={
                  <FormattedMessage
                    id="CLEANER.PROFILE.AVAILABILITY.START.TIME"
                    defaultMessage="Start Time"
                  />
                }
                value={sharedStartTime}
                inputClass="rounded-lg bg-white text-black"
                labelClass="mt-2 text-black"
                InputDivClass="mt-0 pr-2 pb-2 w-[50%]"
                placeholder={
                  <FormattedMessage
                    id="CLEANER.PROFILE.AVAILABILITY.START.TIME"
                    defaultMessage="Start Time"
                  />
                }
              />
              <ReactTimePicker
                onChange={(value: string) =>
                  handleAvailability({ type: "sharedEnd", value })
                }
                label={
                  <FormattedMessage
                    id="CLEANER.PROFILE.AVAILABILITY.END.TIME"
                    defaultMessage="End Time"
                  />
                }
                value={sharedEndTime}
                inputClass="rounded-lg bg-white text-black"
                labelClass="mt-2 text-black"
                InputDivClass="mt-0 pr-2 pb-2 w-[50%]"
                placeholder={
                  <FormattedMessage
                    id="CLEANER.PROFILE.AVAILABILITY.END.TIME"
                    defaultMessage="End Time"
                  />
                }
              />
            </div>
          )}
          <div className="mb-4 text-gray-500 text-sm font-semibold">OR</div>
          {Days.map((item, index) => (
            <>
              <div
                key={index}
                className="flex space-x-3 w-full mb-8 items-center"
              >
                <label
                  htmlFor={`day-${item?.fullName}`}
                  className={`${
                    checkIncluded(item?.fullName)
                      ? "bg-[#ff39b3] font-bold text-white"
                      : "bg-white border border-neutral-200"
                  } rounded-[8px] h-10 px-3 w-28 flex justify-center items-center mt-4 cursor-pointer select-none`}
                >
                  <input
                    id={`day-${item?.fullName}`}
                    checked={checkIncluded(item?.fullName)}
                    type="checkbox"
                    className="mr-2 cursor-pointer w-4 h-4 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 focus:ring-2"
                    onChange={() =>
                      handleAvailability({ day: item?.fullName, type: "day" })
                    }
                  />{" "}
                  {item?.short}
                </label>

                <ReactTimePicker
                  disable={!checkIncluded(item?.fullName) || sameTimeAllDays}
                  onChange={(value: string) =>
                    handleAvailability({
                      day: item?.fullName,
                      startTime: value,
                      type: "startTime",
                    })
                  }
                  label={
                    <FormattedMessage
                      id="CLEANER.PROFILE.AVAILABILITY.START.TIME"
                      defaultMessage="Start Time"
                    />
                  }
                  value={
                    Availability?.find((data) => data?.day === item?.fullName)
                      ?.start_time || ""
                  }
                  inputClass={`rounded-lg ${
                    !checkIncluded(item?.fullName) || sameTimeAllDays
                      ? "!bg-neutral-200 !text-gray-400 !cursor-not-allowed"
                      : "bg-white text-black"
                  }`}
                  labelClass="mt-2 text-black"
                  InputDivClass="mt-0 pr-2 pb-2 w-[50%]"
                  placeholder={
                    <FormattedMessage
                      id="CLEANER.PROFILE.AVAILABILITY.START.TIME"
                      defaultMessage="Start Time"
                    />
                  }
                ></ReactTimePicker>
                <ReactTimePicker
                  disable={!checkIncluded(item?.fullName) || sameTimeAllDays}
                  onChange={(value: string) =>
                    handleAvailability({
                      day: item?.fullName,
                      endTime: value,
                      type: "endTime",
                    })
                  }
                  label={
                    <FormattedMessage
                      id="CLEANER.PROFILE.AVAILABILITY.END.TIME"
                      defaultMessage="End Time"
                    />
                  }
                  value={
                    Availability?.find((data) => data?.day === item?.fullName)
                      ?.end_time || ""
                  }
                  inputClass={`rounded-lg ${
                    !checkIncluded(item?.fullName) || sameTimeAllDays
                      ? "!bg-neutral-200 !text-gray-400 !cursor-not-allowed"
                      : "bg-white text-black"
                  }`}
                  labelClass="mt-2 text-black"
                  InputDivClass="mt-0 pr-2 pb-2 w-[50%]"
                  placeholder={
                    <FormattedMessage
                      id="CLEANER.PROFILE.AVAILABILITY.END.TIME"
                      defaultMessage="End Time"
                    />
                  }
                ></ReactTimePicker>
              </div>
              {customError?.[item?.fullName] && (
                <p className="w-full pr-14 -mt-8 items-center text-red-400 md:text-sm sm:text-[.65rem] mb-3">
                  {customError[item?.fullName]}
                </p>
              )}
            </>
          ))}
          {errors?.availability && (
            <p className="mt-1 text-red-400 md:text-sm sm:text-[.65rem]">
              {validationErrorHandle(errors?.availability)}
            </p>
          )}
          <div className="mt-5 flex space-x-5">
            {(page === "popup" && type === "add") || type === "put" ? (
              ""
            ) : (
              <>
                <ButtonAuth onClick={handleSubmit} loader={buttonLoader}>
                  <FormattedMessage
                    id="REGISTER.BUTTON.SAVEANDCONTINUE"
                    defaultMessage="Save and Continue"
                  />
                </ButtonAuth>
                {/* <div className='rounded-full text-center
                                   bg-[#ff39b3] font-bold
                                   p-2
                                   text-white
                                   w-44
                                   cursor-pointer mb-5 mt-5
                                   hover:bg-navy' onClick={()=> HandleStepper(7)}>
                                    Skip  </div> */}
              </>
            )}
          </div>
        </div>
      )}
    </ContentWithSpinner>
  );
};
export default forwardRef(Availability);
